import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSearchParams } from 'react-router-dom';
import { Translate, withLocalize } from 'react-localize-redux';
import { PageWrapper, PageHeader } from '../components/partials';
import Spinner from '../components/widgets/Spinner';
import Button from '../components/atoms/Button';
import { IconSizes } from '../components/foundations/Icon';
import GeneralPopup, { PopupTypes } from '../components/organisms/GeneralPopup';
import toaster from '../utils/toaster';
import { ensureINSToken, handleFailedRefresh } from '../services/interactiveServiceFetch';
import useAuthedChat from '../chatView/hooks/useAuthedChat';
import useSessionUrlSync from '../chatView/hooks/useSessionUrlSync';
import StreamMonitor from '../chatView/components/StreamMonitor';
import SessionNoticeToasts from '../chatView/components/SessionNoticeToasts';
import { EVENT_TYPES, SESSION_TYPES } from '../chatView/constants';
import useMonitors from './hooks/useMonitors';
import { MonitorApiError, suggestMonitorTitle } from './service';
import AddMonitorBar from './components/AddMonitorBar';
import WatchlistEmptyState from './components/WatchlistEmptyState';
import WatchlistExamplesStrip from './components/WatchlistExamplesStrip';
import MonitorCard from './components/MonitorCard';
import SummaryChips from './components/SummaryChips';
import MonitorDialog from './components/MonitorDialog';
import ScheduleModal from './components/ScheduleModal';
import ThreadShelf from './components/ThreadShelf';
import {
    CARD_STATES,
    DEFAULT_SCHEDULE,
    MONITOR_STATUS,
    MONITOR_VERDICT_EVENT,
    RUN_ERROR_TOAST_KEYS,
    SLACK_AUTHORIZATION_NOT_COMPLETED,
    SLACK_CONNECTED_PARAM,
    SLACK_ERROR_PARAM,
    TOAST_AUTO_CLOSE_MS,
    TRANSLATION_KEY,
    WATCHLIST_EVENTS,
    buildMonitorSlackAuthUrl,
    trackWatchlistEvent,
} from './constants';
import { getCardState, getDisplayVerdict, presetToCron } from './utils';
import css from './page.css';

const EMPTY_DRAFT_MONITOR = { prompt: '', title: '', cron: presetToCron(DEFAULT_SCHEDULE) };

const Watchlist = ({ translate }) => {
    const [openMonitorId, setOpenMonitorId] = useState(null);
    const { chat, authLoading, authError } = useAuthedChat({
        sessionType: SESSION_TYPES.MONITOR,
        isViewingConversation: openMonitorId != null,
    });
    const monitorsApi = useMonitors({ attachRun: chat.attachRun, enabled: !authLoading && !authError });
    const { monitors, loadError, liveState } = monitorsApi;

    const [draftMonitor, setDraftMonitor] = useState(EMPTY_DRAFT_MONITOR);
    const [monitorDialog, setMonitorDialog] = useState(null);
    const [dialogSaving, setDialogSaving] = useState(false);
    const [dialogError, setDialogError] = useState(null);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState(null);
    const [draftScheduleOpen, setDraftScheduleOpen] = useState(false);
    const [monitorToDelete, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [runningAll, setRunningAll] = useState(false);
    const [slackConnectingId, setSlackConnectingId] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();

    const lastPickedExampleRef = useRef('');

    const pickExample = useCallback(text => {
        const lastPicked = lastPickedExampleRef.current;
        lastPickedExampleRef.current = text;
        setDraftMonitor(current =>
            (current.prompt.trim() && current.prompt !== lastPicked ? current : { ...current, prompt: text }));
    }, []);

    const live = useMemo(
        () => ({ ...liveState, streamingSessionIds: chat.streamingSessionIds }),
        [liveState, chat.streamingSessionIds]
    );

    const cardStates = useMemo(
        () => new Map((monitors ?? []).map(m => [m.monitor_id, getCardState(m, live)])),
        [monitors, live]
    );

    const showErrorToast = key =>
        toaster.error(translate(`${TRANSLATION_KEY}.TOASTS.${key}`), { autoClose: TOAST_AUTO_CLOSE_MS });

    useEffect(() => {
        const connectedChannel = searchParams.get(SLACK_CONNECTED_PARAM);
        const slackError = searchParams.get(SLACK_ERROR_PARAM);
        if (!connectedChannel && !slackError) return;
        if (connectedChannel) {
            trackWatchlistEvent(WATCHLIST_EVENTS.SLACK_CONNECTED, { channel: connectedChannel });
            toaster.success(translate(`${TRANSLATION_KEY}.TOASTS.SLACK_CONNECTED`, { channel: connectedChannel }),
                { autoClose: TOAST_AUTO_CLOSE_MS });
        } else if (slackError === SLACK_AUTHORIZATION_NOT_COMPLETED) {
            toaster.info(translate(`${TRANSLATION_KEY}.TOASTS.SLACK_CONNECT_NOT_COMPLETED`),
                { autoClose: TOAST_AUTO_CLOSE_MS });
        } else {
            showErrorToast('SLACK_CONNECT_FAILED');
        }
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            [SLACK_CONNECTED_PARAM, SLACK_ERROR_PARAM].forEach(param => next.delete(param));
            return next;
        }, { replace: true });
    }, [searchParams]);

    useEffect(() => {
        const clearConnectingOnRestore = event => {
            if (event.persisted) setSlackConnectingId(null);
        };
        window.addEventListener('pageshow', clearConnectingOnRestore);
        return () => window.removeEventListener('pageshow', clearConnectingOnRestore);
    }, []);

    const handleConnectSlack = async monitor => {
        trackWatchlistEvent(WATCHLIST_EVENTS.SLACK_CLICKED, { monitor_id: monitor.monitor_id });
        setSlackConnectingId(monitor.monitor_id);
        const refreshResult = await ensureINSToken();
        if (!refreshResult.ok) {
            setSlackConnectingId(null);
            showErrorToast('SLACK_CONNECT_FAILED');
            handleFailedRefresh(refreshResult);
            return;
        }
        window.location.href = buildMonitorSlackAuthUrl(monitor.monitor_id);
    };

    const closeShelf = useCallback(() => setOpenMonitorId(null), []);

    const handleMonitorGone = useCallback(monitor => {
        monitorsApi.removeMonitorLocally(monitor.monitor_id);
        chat.purgeSessionState(monitor.session_id);
        setOpenMonitorId(current => (current === monitor.monitor_id ? null : current));
        showErrorToast('MONITOR_DELETED');
    }, [monitorsApi.removeMonitorLocally, chat.purgeSessionState]);

    const handleStreamEvent = useCallback((sessionId, runId, message) => {
        if (message.type === MONITOR_VERDICT_EVENT) monitorsApi.onCheckVerdict(sessionId, runId, message.data);
        else if (message.type === EVENT_TYPES.FINISH) monitorsApi.onCheckFinish(sessionId, runId, message.finishReason);
        chat.handleStreamMessage(sessionId, message);
    }, [monitorsApi.onCheckVerdict, monitorsApi.onCheckFinish, chat.handleStreamMessage]);

    const openThread = useCallback(monitor => {
        trackWatchlistEvent(WATCHLIST_EVENTS.OPEN_THREAD, { monitor_id: monitor.monitor_id });
        setOpenMonitorId(monitor.monitor_id);
        chat.loadSession(monitor.session_id);
    }, [chat.loadSession]);

    const openMonitor = monitors?.find(m => m.monitor_id === openMonitorId) ?? null;
    const dialogMonitor = monitorDialog?.monitor ?? null;

    const openThreadFromUrl = useCallback(sessionId => {
        const monitor = monitors?.find(m => m.session_id === sessionId);
        if (monitor) openThread(monitor);
    }, [monitors, openThread]);

    useSessionUrlSync({
        ready: !authLoading && !authError && monitors != null,
        openSessionId: openMonitor?.session_id ?? null,
        onOpenFromUrl: openThreadFromUrl,
        onUrlCleared: closeShelf,
    });

    const handleCreate = async userQuery => {
        setCreating(true);
        setCreateError(null);
        try {
            await monitorsApi.addMonitor(userQuery, draftMonitor.cron, draftMonitor.title.trim());
            setDraftMonitor(prev => ({ ...EMPTY_DRAFT_MONITOR, cron: prev.cron }));
            return true;
        } catch (error) {
            if (error instanceof MonitorApiError && error.status === 429) {
                setCreateError(translate(`${TRANSLATION_KEY}.ADD_BAR.QUOTA_ERROR`));
            } else if (error instanceof MonitorApiError && error.status === 422 && error.detail) {
                setCreateError(error.detail);
            } else {
                showErrorToast('GENERIC_ERROR');
            }
            return false;
        } finally {
            setCreating(false);
        }
    };

    const handleRunNow = async monitor => {
        try {
            await monitorsApi.recheck(monitor);
        } catch (error) {
            if (!(error instanceof MonitorApiError)) return showErrorToast('GENERIC_ERROR');
            if (error.status === 404) return handleMonitorGone(monitor);
            if (error.status === 409 && RUN_ERROR_TOAST_KEYS[error.detail]) {
                return toaster.error(translate(RUN_ERROR_TOAST_KEYS[error.detail]), { autoClose: TOAST_AUTO_CLOSE_MS });
            }
            if (error.status === 502) return showErrorToast('RUN_FAILED');
            return showErrorToast('GENERIC_ERROR');
        }
    };

    const handleRunAll = async () => {
        if (runningAll) return;
        setRunningAll(true);
        try {
            await monitorsApi.recheckAllMonitors();
        } catch {
            showErrorToast('GENERIC_ERROR');
        } finally {
            setRunningAll(false);
        }
    };

    const handlePauseToggle = async monitor => {
        try {
            await monitorsApi.setPaused(monitor.monitor_id, monitor.status !== MONITOR_STATUS.PAUSED);
        } catch (error) {
            if (error instanceof MonitorApiError && error.status === 404) return handleMonitorGone(monitor);
            if (error instanceof MonitorApiError && error.status === 409) return showErrorToast('UNPAUSE_IN_PROGRESS');
            showErrorToast('GENERIC_ERROR');
        }
    };

    const handleDisconnectSlack = async monitor => {
        try {
            await monitorsApi.disconnectSlack(monitor.monitor_id);
        } catch (error) {
            if (error instanceof MonitorApiError && error.status === 404) return handleMonitorGone(monitor);
            showErrorToast('GENERIC_ERROR');
        }
    };

    const handleSaveTitle = async (monitor, title) => {
        try {
            await monitorsApi.saveMonitor(monitor.monitor_id, { title });
        } catch (error) {
            if (error instanceof MonitorApiError && error.status === 404) return handleMonitorGone(monitor);
            showErrorToast('GENERIC_ERROR');
            throw error;
        }
    };

    const handleMonitorDialogSave = async ({ title, prompt, cron }) => {
        if (!dialogMonitor) {
            setDraftMonitor({ prompt, title, cron });
            setMonitorDialog(null);
            return;
        }
        setDialogSaving(true);
        setDialogError(null);
        try {
            await monitorsApi.saveMonitor(dialogMonitor.monitor_id, { user_query: prompt, title, schedule: cron });
            setMonitorDialog(null);
        } catch (error) {
            if (error instanceof MonitorApiError && error.status === 404) {
                handleMonitorGone(dialogMonitor);
                setMonitorDialog(null);
            } else if (error instanceof MonitorApiError && error.status === 422 && error.detail) {
                setDialogError(error.detail);
            } else {
                showErrorToast('GENERIC_ERROR');
            }
        } finally {
            setDialogSaving(false);
        }
    };

    const handleSuggestTitle = async userQuery => {
        trackWatchlistEvent(WATCHLIST_EVENTS.SUGGEST_TITLE, {
            monitor_id: dialogMonitor?.monitor_id ?? null,
        });
        try {
            const { title } = await suggestMonitorTitle(userQuery);
            if (!title) showErrorToast('GENERIC_ERROR');
            return title;
        } catch {
            showErrorToast('GENERIC_ERROR');
            return null;
        }
    };

    const handleDraftScheduleSave = cron => {
        setDraftMonitor(prev => ({ ...prev, cron }));
        setDraftScheduleOpen(false);
    };

    const handleDeleteConfirmed = async () => {
        setDeleting(true);
        try {
            await monitorsApi.removeMonitor(monitorToDelete);
            chat.purgeSessionState(monitorToDelete.session_id);
            setOpenMonitorId(current => (current === monitorToDelete.monitor_id ? null : current));
        } catch {
            showErrorToast('GENERIC_ERROR');
        } finally {
            setDeleting(false);
            setDeleteTarget(null);
        }
    };

    if (authLoading) return <Spinner show expanded />;

    const hasIdleMonitor = (monitors ?? []).some(m => m.status === MONITOR_STATUS.IDLE);

    const addBarProps = {
        prompt: draftMonitor.prompt,
        onPromptChange: prompt => setDraftMonitor(prev => ({ ...prev, prompt })),
        onSubmit: handleCreate,
        scheduleCron: draftMonitor.cron,
        onScheduleClick: () => setDraftScheduleOpen(true),
        onOpenEditor: () => {
            setDialogError(null);
            setMonitorDialog({ monitor: null });
        },
        submitting: creating,
        error: createError,
    };

    const content = () => {
        if (authError || (loadError && monitors == null)) {
            return (
                <div className={css.errorState}>
                    <Translate id={`${TRANSLATION_KEY}.LOAD_ERROR`} />
                    {!authError && (
                        <Button type="secondary" onClick={monitorsApi.fetchNow}>
                            <Translate id={`${TRANSLATION_KEY}.RETRY`} />
                        </Button>
                    )}
                </div>
            );
        }
        if (monitors == null) {
            return (
                <div className={css.list}>
                    {[1, 2, 3].map(i => (
                        <div key={i} data-testid="monitor-skeleton" className={css.skeleton} />
                    ))}
                </div>
            );
        }
        if (!monitors.length) {
            return <WatchlistEmptyState {...addBarProps} onPickSuggestion={pickExample} />;
        }
        return (
            <>
                <AddMonitorBar {...addBarProps} />
                <WatchlistExamplesStrip onPick={pickExample} />
                <div className={css.toolbar}>
                    <SummaryChips monitors={monitors} cardStates={cardStates} liveRuns={liveState.liveRuns} />
                    <Button
                        type="flat"
                        className={css.runAll}
                        Icon="refresh"
                        iconSize={IconSizes.X_SMALL}
                        iconClass={classNames({ [css.spinning]: runningAll })}
                        disabled={runningAll || !hasIdleMonitor}
                        onClick={handleRunAll}
                    >
                        <Translate id={`${TRANSLATION_KEY}.RUN_ALL`} />
                    </Button>
                </div>
                <div className={css.list}>
                    {monitors.map(monitor => (
                        <MonitorCard
                            key={monitor.monitor_id}
                            monitor={monitor}
                            cardState={cardStates.get(monitor.monitor_id)}
                            verdict={getDisplayVerdict(monitor, liveState.liveRuns[monitor.session_id])}
                            hasUpdate={chat.updatedSessionIds.includes(monitor.session_id)}
                            onOpenThread={() => openThread(monitor)}
                            onRunNow={() => handleRunNow(monitor)}
                            onOpenMonitorDialog={() => {
                                setDialogError(null);
                                setMonitorDialog({ monitor });
                            }}
                            onPauseToggle={() => handlePauseToggle(monitor)}
                            onRemove={() => setDeleteTarget(monitor)}
                            onConnectSlack={handleConnectSlack}
                            onDisconnectSlack={() => handleDisconnectSlack(monitor)}
                            slackConnecting={slackConnectingId === monitor.monitor_id}
                        />
                    ))}
                </div>
            </>
        );
    };

    return (
        <PageWrapper className={css.page}>
            <PageHeader text={`${TRANSLATION_KEY}.TITLE`} />
            {content()}
            {chat.streamDescriptors.map(({ key, runId }) => (
                <StreamMonitor
                    key={runId}
                    sessionKey={key}
                    runId={runId}
                    onMessage={(sessionId, message) => handleStreamEvent(sessionId, runId, message)}
                    onError={(sessionId, errorMessage) => chat.handleStreamError(sessionId, errorMessage)}
                />
            ))}
            <SessionNoticeToasts chat={chat} onSessionGone={closeShelf} />
            <MonitorDialog
                open={monitorDialog != null}
                initialTitle={dialogMonitor ? dialogMonitor.title ?? '' : draftMonitor.title}
                initialPrompt={dialogMonitor ? dialogMonitor.user_query : draftMonitor.prompt}
                initialCron={dialogMonitor ? dialogMonitor.schedule : draftMonitor.cron}
                isNew={dialogMonitor == null}
                slackChannelName={dialogMonitor?.slack_channels?.[0]?.channel_name ?? null}
                onSave={handleMonitorDialogSave}
                onClose={() => setMonitorDialog(null)}
                onSuggestTitle={handleSuggestTitle}
                saving={dialogSaving}
                error={dialogError}
            />
            <ScheduleModal
                open={draftScheduleOpen}
                cron={draftMonitor.cron}
                isNew
                onSave={handleDraftScheduleSave}
                onClose={() => setDraftScheduleOpen(false)}
            />
            <ThreadShelf
                open={openMonitor != null}
                monitor={openMonitor}
                verdict={openMonitor ? getDisplayVerdict(openMonitor, liveState.liveRuns[openMonitor.session_id]) : null}
                chat={chat}
                onClose={closeShelf}
                onSaveTitle={title => handleSaveTitle(openMonitor, title)}
                onOpenMonitorDialog={() => {
                    setDialogError(null);
                    setMonitorDialog({ monitor: openMonitor });
                }}
            />
            <GeneralPopup
                open={monitorToDelete != null}
                type={PopupTypes.DELETE}
                title={`${TRANSLATION_KEY}.DELETE_MODAL.TITLE`}
                text={`${TRANSLATION_KEY}.DELETE_MODAL.MESSAGE`}
                acceptText={`${TRANSLATION_KEY}.DELETE_MODAL.ACCEPT`}
                rejectText={`${TRANSLATION_KEY}.DELETE_MODAL.REJECT`}
                onAccept={handleDeleteConfirmed}
                onReject={() => setDeleteTarget(null)}
                showSpinner={deleting}
            />
        </PageWrapper>
    );
};

Watchlist.propTypes = {
    translate: PropTypes.func.isRequired,
};

export default withLocalize(Watchlist);
